<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import router from '@/router';
import ProgressFrame from '../layout/ProgressFrame.vue';
import ButtonPrimary from '@/shared/components/button/ButtonPrimary.vue';
import AddIcon from '@shared/assets/icon/add.svg';
import LoadingSpinner from '@/shared/components/LoadingSpinner.vue';
import { useProjectStore } from '../../stores/projectStore';
import ProjectTile from '../../components/ProjectTile.vue';
import PageLoadFailed from '../../components/PageLoadFailed.vue';

const projectStore = useProjectStore();
const { allProjects: projects } = storeToRefs(projectStore);
const isLoading = ref(true);
const pageLoadFailed = ref(false);

onMounted(async () => {
  await fetchAllProjects();
});

const fetchAllProjects = async () => {
  isLoading.value = true;
  pageLoadFailed.value = false;

  try {
    await projectStore.fetchAllProjects();
  } catch (error) {
    console.error('Failed to fetch all projects', error);
    pageLoadFailed.value = true;
  } finally {
    isLoading.value = false;
  }
};

const goToCreateProject = () => {
  projectStore.resetNewProject();
  router.push({ name: 'ProjectCreate' });
};
</script>

<template>
  <ProgressFrame pageClass="section-page" withBackArrow>
    <template #title>All projects</template>
    <template v-if="isLoading">
      <LoadingSpinner />
    </template>
    <template v-else-if="pageLoadFailed">
      <PageLoadFailed
        title="Failed to load projects"
        :retryButtonExtraTopMargin="true"
        @onRetry="fetchAllProjects"
      />
    </template>
    <template v-else>
      <div class="content">
        <template v-if="projects.length === 0">
          <p class="empty-project-list__text">You don't have any projects.</p>
        </template>
        <template v-else v-for="project in projects" :key="project.id">
          <ProjectTile :project="project" :canEdit="true" :canAddSite="true" />
        </template>
      </div>
      <div class="end-of-page">
        <ButtonPrimary
          class="create-project__button"
          block
          @click="goToCreateProject"
          data-tn="create-project-btn"
        >
          <template #iconLeft>
            <AddIcon />
          </template>
          Create project
        </ButtonPrimary>
      </div>
    </template>
  </ProgressFrame>
</template>

<style lang="less" scoped>
.empty-project-list__text {
  text-align: center;
  color: @color-primary;
  font-weight: @font-weight-regular;
}

.end-of-page {
  width: 100%;
  margin-top: @margin-top-end-of-page-section;
  padding: @content-side-margin;
}
</style>
